import type { XlsxAnchor } from '../types/xlsxAnchor';
import {
  ChatMessage,
  ChatThread,
  CompletenessSummary,
  ModeListResponse,
  SearchResult,
  SwitchModeRequest,
  SwitchModeResponse,
  ThreadModeResponse,
  WebSearchStatus,
} from '../types/chat';
import { v4 as uuidv4 } from 'uuid';
import { FileSearchResponse } from './chatService.types';
import { fetchWithSession } from './fetchWithSession';
import {
  mapFileSearchResult,
  mapMessageSource,
  mapMode,
  mapStreamSearchResult,
  mapStreamSource,
} from './chatService.helpers';

// Use REACT_APP_CHAT_API_URL for chat endpoints to avoid URL duplication
// REACT_APP_API_URL includes /api/v1 suffix, which would cause double path issues
const API_BASE_URL = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:8000';

/**
 * A chat stream that ended in a backend failure rather than an answer.
 *
 * `code` is the backend's machine-readable reason (`storage_unavailable` when
 * nothing could be persisted, `stream_failed` otherwise) so callers can tell a
 * retryable outage from a generation failure.
 */
export class StreamFailedError extends Error {
  constructor(readonly code: string = 'stream_failed') {
    super(`Chat stream failed: ${code}`);
    this.name = 'StreamFailedError';
  }
}

/**
 * Written as `noticeKey:` literals so scripts/audit-locale-health.js records them.
 * Returned from a bare ternary they were invisible to it, and renaming either key
 * left the gate green while a raw key reached the user.
 */
const STREAM_FAILURE_NOTICES = {
  storageUnavailable: { noticeKey: 'chat.storageUnavailable' },
  generation: { noticeKey: 'chat.streamError' },
} as const;

/**
 * i18n key describing a stream failure the backend reported, or null when the
 * failure came from somewhere else (transport, abort) and the caller's own
 * fallback copy applies.
 */
export const streamFailureKey = (error: unknown): string | null =>
  error instanceof StreamFailedError
    ? (error.code === 'storage_unavailable'
      ? STREAM_FAILURE_NOTICES.storageUnavailable
      : STREAM_FAILURE_NOTICES.generation).noticeKey
    : null;

class ChatService {
  /**
   * Get access token from sessionStorage
   */
  private getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Get headers with Authorization token
   * Cookie認証廃止により、Authorization headerのみを使用
   */
  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async createThread(title: string): Promise<ChatThread> {
    const response = await fetchWithSession(`${API_BASE_URL}/api/v1/chat/threads`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        title,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create thread');
    }

    const data = await response.json();

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages: [],
    };
  }

  async getThread(threadId: string): Promise<ChatThread> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get thread');
    }

    const data = await response.json();

    // Get messages for the thread
    const messages = await this.getMessages(threadId);

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages,
    };
  }

  async getMessages(threadId: string): Promise<ChatMessage[]> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/messages`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get messages');
    }

    const data = await response.json();

    return data.messages.map((msg: any) => {
      // Map sources to SearchResult format for SearchResultsCard
      const sources = msg.sources || [];
      const mappedSources = sources.map(mapMessageSource);

      return {
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.created_at),
        sources: sources,
        // Build searchResults if sources exist (for SearchResultsCard display)
        searchResults: mappedSources.length > 0 ? {
          query: msg.metadata?.search_query || '',
          results: mappedSources,
        } : undefined,
        // Hydrate verification + grounding from persisted metadata
        groundingStatus: msg.metadata?.grounding_status,
        completeness: msg.metadata?.completeness,
        literalValues: msg.metadata?.literal_values,
        verificationSummary: msg.metadata?.verification_summary,
        webSearchStatus: msg.metadata?.web_search_status,
      };
    });
  }

  async searchFiles(threadId: string, query: string): Promise<SearchResult[]> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/file-search?` +
      new URLSearchParams({
        q: query,
        limit: '5',
      }),
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to search files');
    }

    const data: FileSearchResponse = await response.json();

    return data.results.map(mapFileSearchResult);
  }

  async streamMessage(
    threadId: string,
    message: string,
    onMessage: (content: string) => void,
    onSearchResults?: (results: SearchResult[]) => void,
    onComplete?: (modeInfo?: {
      mode?: string;
      modeSwitched?: boolean;
      newMode?: string;
      groundingStatus?: string;
      completeness?: CompletenessSummary;
      verificationSummary?: any;
      webSearchStatus?: WebSearchStatus;
    }) => void,
    options?: {
      mode?: string;
      modeData?: Record<string, any>;
      preferredLanguage?: string;
      selectedFiles?: string[];
      xlsxAnchor?: XlsxAnchor;
      includeWebSearch?: boolean;
      onTitleUpdate?: (title: string) => void;
      onLiteralValues?: (values: string[]) => void;
    }
  ): Promise<void> {
    const params: Record<string, string> = { message };
    if (options?.mode) {
      params.mode = options.mode;
    }
    if (options?.modeData) {
      params.mode_data = JSON.stringify(options.modeData);
    }
    if (options?.preferredLanguage) {
      params.preferred_language = options.preferredLanguage;
    }
    if (options?.selectedFiles && options.selectedFiles.length > 0) {
      params.selected_files = options.selectedFiles.join(',');
    }
    if (options?.includeWebSearch) {
      params.include_web = 'true';
    }

    if (options?.xlsxAnchor) params.xlsx_anchor = JSON.stringify(options.xlsxAnchor);
    const endpoint = options?.xlsxAnchor ? 'stream-cells' : 'stream';
    const url = `${API_BASE_URL}/api/v1/chat/threads/${threadId}/${endpoint}?` +
      new URLSearchParams(params);

    const response = await fetchWithSession(url, {
      headers: this.getHeaders(false),
    });

    if (!response.ok) {
      const failure = await response.json().catch(() => ({}));
      throw new Error(options?.xlsxAnchor ? `Excel: ${failure.detail || response.status}` : 'Failed to stream message');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullMessage = '';
    let completed = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data) {
              try {
                const event = JSON.parse(data);

                if (event.type === 'content') {
                  fullMessage += event.data;
                  onMessage(fullMessage);
                } else if (event.type === 'search_results' && onSearchResults) {
                  onSearchResults(event.results.map(mapStreamSearchResult));
                } else if (event.type === 'sources' && onSearchResults) {
                  onSearchResults(event.sources.map(mapStreamSource));
                } else if (event.type === 'literal_values' && options?.onLiteralValues) {
                  options.onLiteralValues(event.values);
                } else if (event.type === 'title_update' && options?.onTitleUpdate) {
                  options.onTitleUpdate(event.title);
                } else if (event.type === 'error') {
                  // The stream failed. Throwing hands the failure to the caller's
                  // catch; swallowing it here left the UI showing an empty reply
                  // that was indistinguishable from a successful one (#1086).
                  throw new StreamFailedError(event.code);
                } else if (event.type === 'completed') {
                  if (event.status === 'error') {
                    throw new StreamFailedError(event.code);
                  }
                  if (completed) continue;
                  completed = true;
                  onComplete?.({
                    mode: event.mode,
                    modeSwitched: event.mode_switched,
                    newMode: event.new_mode,
                    groundingStatus: event.grounding_status,
                    completeness: event.completeness,
                    verificationSummary: event.verification_summary,
                    webSearchStatus: event.web_search_status,
                  });
                }
              } catch (e) {
                if (e instanceof StreamFailedError) {
                  throw e;
                }
                console.error('Failed to parse SSE event:', e);
              }
            }
          }
        }
      }
      // EOF can be a dropped proxy/network connection, including after a
      // partial answer. Only the backend's terminal event proves completion.
      if (!completed) throw new StreamFailedError('stream_incomplete');
    } finally {
      reader.releaseLock();
    }
  }

  async sendMessage(threadId: string, message: string): Promise<ChatMessage> {
    // Use streaming API and collect the response
    let fullResponse = '';
    let searchResults: SearchResult[] = [];

    await this.streamMessage(
      threadId,
      message,
      (content) => {
        fullResponse = content;
      },
      (results) => {
        searchResults = results;
      }
    );

    // Return a ChatMessage object
    // TODO: The streaming API should return the message ID from the server
    // For now, generate a client-side UUID as a placeholder
    return {
      id: uuidv4(),
      role: 'assistant',
      content: fullResponse,
      timestamp: new Date(),
      searchResults: searchResults.length > 0 ? {
        query: message,
        results: searchResults,
      } : undefined,
    };
  }

  async getThreads(): Promise<ChatThread[]> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads?limit=50`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get threads');
    }

    const data = await response.json();

    return data.threads.map((thread: any) => ({
      id: thread.id,
      title: thread.title,
      createdAt: new Date(thread.created_at),
      updatedAt: new Date(thread.updated_at),
      isPinned: Boolean(thread.is_pinned),
      pinnedAt: thread.pinned_at ? new Date(thread.pinned_at) : null,
      messages: [],
    }));
  }

  async updateThreadPin(threadId: string, isPinned: boolean): Promise<ChatThread> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/pin`,
      {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify({ is_pinned: isPinned }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update thread pin');
    }

    const data = await response.json();

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages: [],
    };
  }

  // ============================================================
  // Mode Management API Methods (Issue #302)
  // ============================================================

  /**
   * Get all available chat modes
   */
  async getModes(): Promise<ModeListResponse> {
    const response = await fetchWithSession(`${API_BASE_URL}/api/v1/chat/modes`, {
      headers: this.getHeaders(false),
    });

    if (!response.ok) {
      throw new Error('Failed to get modes');
    }

    const data = await response.json();

    return {
      modes: data.modes.map(mapMode),
      total: data.total,
    };
  }

  /**
   * Get current mode for a thread
   */
  async getThreadMode(threadId: string): Promise<ThreadModeResponse> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/mode`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get thread mode');
    }

    const data = await response.json();

    return {
      thread_id: data.thread_id,
      current_mode: data.current_mode,
      mode: mapMode(data.mode),
    };
  }

  /**
   * Switch mode for a thread
   */
  async switchMode(
    threadId: string,
    request: SwitchModeRequest
  ): Promise<SwitchModeResponse> {
    const response = await fetchWithSession(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/mode`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to switch mode');
    }

    const data = await response.json();

    return {
      mode: mapMode(data.mode),
      message: data.message,
      previous_mode: data.previous_mode,
    };
  }
}

export const chatService = new ChatService();
export default chatService;
